import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api, { getImageUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  MapPin, Users, CalendarDays, Plus, Video, ExternalLink, Upload, X, Search,
  Edit, Trash2, Building2, Sparkles, ChevronUp
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const eventTypeColors = {
  match: "bg-emerald-50 text-emerald-700 border-emerald-100",
  scouting: "bg-blue-50 text-[#074572] border-blue-100",
  workshop: "bg-amber-50 text-amber-700 border-amber-100",
  networking: "bg-purple-50 text-purple-700 border-purple-100",
  tournament: "bg-rose-50 text-rose-700 border-rose-100",
  trial: "bg-cyan-50 text-cyan-700 border-cyan-100",
  camp: "bg-orange-50 text-orange-700 border-orange-100",
  online: "bg-indigo-50 text-indigo-700 border-indigo-100",
};

const EVENT_TYPES = [
  { value: "match", label: "Match" },
  { value: "tournament", label: "Tournament" },
  { value: "workshop", label: "Workshop" },
  { value: "scouting", label: "Scouting" },
  { value: "trial", label: "Trial" },
  { value: "camp", label: "Camp" },
  { value: "networking", label: "Networking" },
  { value: "online", label: "Online Session" },
];

const placeholderImages = [
  "https://images.unsplash.com/photo-1721676256105-aba5e2e0cb42?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwzfHxzb2NjZXIlMjBzdGFkaXVtJTIwY29hY2h8ZW58MHx8fHwxNzc2Mjg4NTgzfDA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1721678385016-de79d74efe3d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxzb2NjZXIlMjBzdGFkaXVtJTIwY29hY2h8ZW58MHx8fHwxNzc2Mjg4NTgzfDA&ixlib=rb-4.1.0&q=85",
];

function formatDateRange(startIso, endIso) {
  if (!startIso) return { dateLabel: "TBD", timeLabel: "" };
  const start = new Date(startIso);
  const end = endIso ? new Date(endIso) : null;
  const opts = { month: "short", day: "numeric" };
  const yearOpt = { year: "numeric" };
  const timeOpts = { hour: "2-digit", minute: "2-digit" };
  const startTime = start.toLocaleTimeString(undefined, timeOpts);
  if (!end || Math.abs(end.getTime() - start.getTime()) < 60 * 60 * 1000) {
    return {
      dateLabel: `${start.toLocaleDateString(undefined, opts)}, ${start.toLocaleDateString(undefined, yearOpt)}`,
      timeLabel: startTime,
    };
  }
  const endTime = end.toLocaleTimeString(undefined, timeOpts);
  const sameDay = start.toDateString() === end.toDateString();
  if (sameDay) {
    return {
      dateLabel: `${start.toLocaleDateString(undefined, opts)}, ${start.toLocaleDateString(undefined, yearOpt)}`,
      timeLabel: `${startTime} – ${endTime}`,
    };
  }
  return {
    dateLabel: `${start.toLocaleDateString(undefined, opts)} – ${end.toLocaleDateString(undefined, opts)}, ${end.toLocaleDateString(undefined, yearOpt)}`,
    timeLabel: `${startTime} – ${endTime}`,
  };
}

function InfoBlock({ Icon, label, value, sub, iconColor = "#074572", iconBg = "#0745720d", valueClass = "" }) {
  return (
    <div className="flex items-start gap-2 min-w-0" data-testid={`event-info-${label}`}>
      <div className="w-9 h-9 rounded-md flex items-center justify-center shrink-0" style={{ backgroundColor: iconBg }}>
        <Icon size={17} style={{ color: iconColor }} strokeWidth={1.75} />
      </div>
      <div className="min-w-0 leading-tight pt-0.5">
        <p className={`text-[12.5px] font-semibold text-zinc-900 truncate ${valueClass}`} title={value}>{value}</p>
        {sub && <p className="text-[10.5px] text-zinc-500 truncate mt-0.5" title={sub}>{sub}</p>}
      </div>
    </div>
  );
}

/* -------------------- FILTER CHECKBOX GROUP -------------------- */
function FilterGroup({ title, items, value, onChange, testId, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  const isAllSelected = value.length === 0 || value.length === items.length;

  const toggle = (id) => {
    if (id === "all") {
      onChange([]);
      return;
    }
    let next;
    const set = new Set(value.length === 0 ? [] : value);
    if (set.has(id)) set.delete(id); else set.add(id);
    next = Array.from(set);
    onChange(next);
  };

  return (
    <div className="border-b border-[#E6E6E6] last:border-b-0 py-3 first:pt-0" data-testid={testId}>
      <button type="button" onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between mb-2" data-testid={`${testId}-toggle`}>
        <span className="text-[11px] font-bold text-zinc-800 uppercase tracking-wider">{title}</span>
        <ChevronUp size={13} className={`text-zinc-400 transition-transform ${open ? "" : "rotate-180"}`} />
      </button>
      {open && (
        <div className="space-y-1.5">
          {items.map(it => {
            const checked = it.id === "all" ? isAllSelected : value.includes(it.id);
            return (
              <label
                key={it.id}
                className="flex items-center justify-between gap-2 cursor-pointer group py-1"
                data-testid={`${testId}-${it.id}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(it.id)}
                    className="w-4 h-4 accent-[#074572] rounded shrink-0"
                  />
                  <span className={`text-sm truncate ${checked ? "text-zinc-900 font-medium" : "text-zinc-600 group-hover:text-zinc-900"}`}>
                    {it.label}
                  </span>
                </div>
                <span className="text-[11px] text-zinc-400 tabular-nums shrink-0">{it.count}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* -------------------- EVENT CARD -------------------- */
function EventCard({ event, myId, onJoin, onOpenDetail, onEdit }) {
  const status = event.status || (() => {
    const dt = event.date ? new Date(event.date) : null;
    const now = new Date();
    if (dt && dt < now) return "completed";
    const cap = event.max_participants;
    if (cap && (event.participants?.length || 0) >= cap) return "sold_out";
    return "upcoming";
  })();
  const isOwn = event.organizer_id === myId;
  const isCompleted = status === "completed";
  const isSoldOut = status === "sold_out";
  const joined = event.participants?.includes(myId);
  const img = event.image_url
    ? (event.image_url.startsWith("http") ? event.image_url : getImageUrl(event.image_url))
    : placeholderImages[Math.abs((event.id || "").length) % placeholderImages.length];
  const { dateLabel, timeLabel } = formatDateRange(event.date, event.end_date);
  const attendees = event.participants?.length || 0;
  const cap = event.max_participants;
  const attendedLabel = cap ? `${attendees} / ${cap}` : `${attendees}`;
  const orgName = event.organizer_display_name || event.organizer?.name || event.organizer_name;
  const orgImage = event.organizer?.profile_image ? getImageUrl(event.organizer.profile_image) : null;
  const sponsors = (event.sponsors || []).slice(0, 4);
  const typeLabel = EVENT_TYPES.find(t => t.value === event.event_type)?.label || event.event_type || "Event";
  const [locPrimary, locSecondary] = (event.location || "").includes("·")
    ? event.location.split("·").map(s => s.trim())
    : [event.location || "TBA", ""];

  return (
    <div
      className={`relative bg-white border border-[#E6E6E6] rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow flex flex-col ${isCompleted ? "grayscale opacity-90" : ""}`}
      data-testid={`event-card-${event.id}`}
      data-status={status}
    >
      {/* Ribbon: Sold Out / Completed */}
      {(isSoldOut || isCompleted) && (
        <div
          className={`absolute top-3 right-3 z-10 px-3 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider text-white shadow ${
            isCompleted ? "bg-zinc-700" : "bg-[#F85037]"
          }`}
          data-testid={`event-ribbon-${event.id}`}
        >
          {isCompleted ? "Completed" : "Sold Out"}
        </div>
      )}

      {/* Owner Edit corner */}
      {isOwn && !isCompleted && !isSoldOut && (
        <button
          onClick={(e) => { e.stopPropagation(); onEdit(event); }}
          className="absolute top-3 right-3 z-10 px-3 py-1 rounded-md text-[11px] font-semibold text-[#074572] bg-white border border-[#074572]/40 hover:bg-[#074572]/5 shadow-sm flex items-center gap-1"
          data-testid={`event-edit-btn-${event.id}`}
        >
          <Edit size={12} /> Edit
        </button>
      )}

      <div className="flex flex-col sm:flex-row flex-1">
        {/* Square image with padding + rounded corners */}
        <div className="p-4 sm:pr-0 shrink-0">
          <button
            type="button"
            onClick={() => onOpenDetail(event)}
            className="relative bg-zinc-100 w-full sm:w-48 aspect-square overflow-hidden rounded-xl"
            data-testid={`event-image-${event.id}`}
          >
            <img src={img} alt={event.title} className="w-full h-full object-cover" />
          </button>
        </div>

        <div className="flex-1 p-4 flex flex-col min-w-0">
          {/* Type badge */}
          <div className="flex items-center gap-1.5">
            <Badge className={`text-[10px] font-medium border ${eventTypeColors[event.event_type] || "bg-zinc-50 text-zinc-600 border-zinc-100"}`}>
              {typeLabel}
            </Badge>
            {event.online_url && (
              <Badge className="text-[10px] bg-indigo-50 text-indigo-700 border border-indigo-100 gap-0.5">
                <Video size={9} /> Online
              </Badge>
            )}
          </div>

          {/* Title — fixed 2-line height */}
          <button
            onClick={() => onOpenDetail(event)}
            className="text-left mt-2"
            data-testid={`event-title-${event.id}`}
          >
            <h3
              className="text-[17px] font-semibold text-zinc-900 leading-snug hover:text-[#074572] transition-colors"
              style={{ fontFamily: 'Outfit', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', height: '2.5em' }}
            >
              {event.title}
            </h3>
          </button>

          {/* Description — fixed 3-line height */}
          <p
            className="text-[13px] text-zinc-500 mt-1 leading-relaxed"
            style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', height: '4.2em' }}
            data-testid={`event-desc-${event.id}`}
          >
            {event.description || "\u00a0"}
          </p>

          {/* Organizer */}
          <Link
            to={event.organizer?._id ? `/profile/${event.organizer._id}` : "#"}
            onClick={(e) => { if (!event.organizer?._id) e.preventDefault(); }}
            className="mt-3 inline-flex items-center gap-2 group max-w-full h-7"
            data-testid={`event-organizer-${event.id}`}
          >
            <Avatar className="h-6 w-6 border border-[#E6E6E6]">
              {orgImage ? <AvatarImage src={orgImage} /> : null}
              <AvatarFallback className="bg-[#074572] text-white text-[9px] font-semibold">
                {(orgName || "?").split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2)}
              </AvatarFallback>
            </Avatar>
            <p className="text-[13px] font-semibold text-zinc-900 group-hover:text-[#074572] truncate">{orgName}</p>
          </Link>
        </div>
      </div>

      {/* Full-width info row */}
      <div className="grid grid-cols-3 gap-2 px-4 py-3 border-t border-[#E6E6E6]">
        <InfoBlock
          Icon={CalendarDays}
          label="date"
          value={dateLabel}
          sub={timeLabel}
          iconColor="#074572"
          iconBg="#0745720d"
        />
        <InfoBlock
          Icon={MapPin}
          label="location"
          value={locPrimary}
          sub={locSecondary}
          iconColor="#074572"
          iconBg="#0745720d"
        />
        <InfoBlock
          Icon={Users}
          label="attendees"
          value={attendedLabel}
          sub={isSoldOut ? "Full" : "Attending"}
          iconColor="#F85037"
          iconBg="#F850370d"
          valueClass={isSoldOut ? "text-[#F85037]" : "text-emerald-600"}
        />
      </div>

      {/* Sponsors row (always rendered for consistent height) */}
      <div className="px-4 py-2.5 border-t border-dashed border-[#E6E6E6] flex items-center gap-3 h-11" data-testid={`event-sponsors-${event.id}`}>
        {sponsors.length > 0 ? (
          <>
            <span className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider shrink-0">Sponsored by</span>
            <div className="flex items-center gap-4 flex-1 overflow-hidden">
              {sponsors.map((s, i) => (
                <div key={i} className="h-5 flex items-center shrink-0" title={s.name}>
                  {s.logo_url ? (
                    <img src={s.logo_url.startsWith("http") ? s.logo_url : getImageUrl(s.logo_url)} alt={s.name} className="h-full w-auto max-w-[64px] object-contain grayscale opacity-80" />
                  ) : (
                    <span className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wide">{s.name}</span>
                  )}
                </div>
              ))}
            </div>
          </>
        ) : (
          <span className="text-[10px] text-zinc-300 italic">No sponsors yet</span>
        )}
      </div>

      {/* Actions — 2 equal-width buttons */}
      <div className="grid grid-cols-2 gap-2 p-4 pt-3">
        {isOwn ? (
          <>
            <Button variant="outline" onClick={() => onEdit(event)} className="border-[#074572]/40 text-[#074572] hover:bg-[#074572]/5 h-10 font-medium" data-testid={`event-manage-btn-${event.id}`}>
              <Edit size={13} className="mr-1.5" /> Manage
            </Button>
            <Button onClick={() => onOpenDetail(event)} className="bg-[#F85037] hover:bg-[#e04530] text-white h-10 font-medium" data-testid={`event-view-btn-${event.id}`}>
              View Details
            </Button>
          </>
        ) : isCompleted ? (
          <>
            <Button variant="outline" disabled className="border-[#E6E6E6] text-zinc-400 h-10" data-testid={`event-completed-${event.id}`}>Completed</Button>
            <Button variant="outline" onClick={() => onOpenDetail(event)} className="border-[#074572]/40 text-[#074572] h-10 font-medium" data-testid={`event-view-btn-${event.id}`}>
              View Details
            </Button>
          </>
        ) : isSoldOut ? (
          <>
            <Button variant="outline" disabled className="border-[#F85037]/30 text-[#F85037] h-10 font-medium" data-testid={`event-soldout-${event.id}`}>Sold Out</Button>
            <Button onClick={() => onOpenDetail(event)} className="bg-[#074572] hover:bg-[#053a60] text-white h-10 font-medium" data-testid={`event-view-btn-${event.id}`}>
              View Details
            </Button>
          </>
        ) : (
          <>
            <Button variant="outline" onClick={() => onOpenDetail(event)} className="border-[#074572]/40 text-[#074572] hover:bg-[#074572]/5 h-10 font-medium" data-testid={`event-view-btn-${event.id}`}>
              View Details
            </Button>
            <Button
              onClick={() => onJoin(event.id)}
              className={`h-10 text-white font-medium ${joined ? "bg-[#074572] hover:bg-[#053a60]" : "bg-[#F85037] hover:bg-[#e04530]"}`}
              data-testid={`event-join-${event.id}`}
            >
              {joined ? "Leave" : "Join Event"}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

/* -------------------- MAIN PAGE -------------------- */
export default function EventsPage() {
  const { user } = useAuth();
  const myId = user?._id || user?.id;
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({ status: { upcoming: 0, completed: 0, sold_out: 0 }, types: {}, total: 0 });
  const [statusFilter, setStatusFilter] = useState([]); // multi-select: [] = all
  const [typeFilter, setTypeFilter] = useState([]); // multi-select: [] = all
  const [sortBy, setSortBy] = useState("upcoming");
  const [dateFilter, setDateFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [organizerFilter, setOrganizerFilter] = useState("");
  const [search, setSearch] = useState("");
  const [showMineOnly, setShowMineOnly] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailEvent, setDetailEvent] = useState(null);

  useEffect(() => {
    api.get("/events/counts").then(r => setCounts(r.data || { status: {}, types: {}, total: 0 })).catch(() => {});
  }, [events]);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("sort", sortBy);
      if (showMineOnly) params.set("mine", "true");
      if (search.trim()) params.set("search", search.trim());
      params.set("limit", 60);
      const { data } = await api.get(`/events?${params.toString()}`);
      setEvents(data.events || []);
    } catch { toast.error("Failed to load events"); }
    finally { setLoading(false); }
  }, [sortBy, search, showMineOnly]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  const filteredEvents = useMemo(() => {
    return events.filter(e => {
      if (statusFilter.length > 0 && !statusFilter.includes(e.status)) return false;
      if (typeFilter.length > 0 && !typeFilter.includes(e.event_type)) return false;
      if (dateFilter) {
        try {
          const evDate = new Date(e.date).toISOString().slice(0, 10);
          if (evDate !== dateFilter) return false;
        } catch { return false; }
      }
      if (locationFilter && !e.location?.toLowerCase().includes(locationFilter.toLowerCase())) return false;
      if (organizerFilter) {
        const org = (e.organizer_display_name || e.organizer_name || e.organizer?.name || "").toLowerCase();
        if (!org.includes(organizerFilter.toLowerCase())) return false;
      }
      return true;
    });
  }, [events, statusFilter, typeFilter, dateFilter, locationFilter, organizerFilter]);

  const handleJoin = async (eventId) => {
    try {
      const { data } = await api.post(`/events/${eventId}/join`);
      toast.success(data.joined ? "Joined event!" : "Left event");
      setEvents(prev => prev.map(e => {
        if (e.id !== eventId) return e;
        const participants = data.joined
          ? [...(e.participants || []), myId]
          : (e.participants || []).filter(p => p !== myId);
        return { ...e, participants };
      }));
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const openDetail = async (event) => {
    try {
      const { data } = await api.get(`/events/${event.id}`);
      setDetailEvent(data);
      setDetailOpen(true);
    } catch { toast.error("Failed to open event"); }
  };

  const openEdit = (event) => { setEditEvent(event); setCreateOpen(true); };

  const handleDelete = async (event) => {
    if (!window.confirm(`Delete "${event.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/events/${event.id}`);
      toast.success("Event deleted");
      setEvents(prev => prev.filter(e => e.id !== event.id));
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to delete"); }
  };

  const clearFilters = () => {
    setStatusFilter([]); setTypeFilter([]); setDateFilter(""); setLocationFilter(""); setOrganizerFilter(""); setSearch(""); setShowMineOnly(false);
  };
  const hasFilters = statusFilter.length > 0 || typeFilter.length > 0 || search || dateFilter || locationFilter || organizerFilter || showMineOnly;

  const typeLabels = { match: "Match", tournament: "Tournament", workshop: "Workshop", scouting: "Scouting / Trial", trial: "Trial", camp: "Camp", networking: "Networking", online: "Online Session" };
  const orderedTypes = Object.entries(counts.types || {}).sort((a, b) => b[1] - a[1]);
  const typeItems = [
    { id: "all", label: "All Types", count: counts.total || 0 },
    ...orderedTypes.map(([id, count]) => ({ id, label: typeLabels[id] || id.charAt(0).toUpperCase() + id.slice(1), count })),
  ];
  const statusItems = [
    { id: "upcoming", label: "Upcoming", count: counts.status?.upcoming || 0 },
    { id: "completed", label: "Completed", count: counts.status?.completed || 0 },
    { id: "sold_out", label: "Sold Out", count: counts.status?.sold_out || 0 },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="events-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="text-3xl font-medium text-zinc-900 relative inline-block" style={{ fontFamily: 'Outfit' }}>
            Events
            <span className="absolute -bottom-1 left-0 w-10 h-[3px] bg-[#074572] rounded-full" />
          </h1>
          <p className="text-sm text-zinc-500 mt-2">Discover football events, online sessions, and trials.</p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowMineOnly(m => !m)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${showMineOnly ? "bg-[#074572] border-[#074572] text-white" : "bg-white border-[#E6E6E6] text-zinc-600 hover:border-[#074572]/40"}`}
              data-testid="events-mine-toggle"
            >
              My events
            </button>
            <Button onClick={() => { setEditEvent(null); setCreateOpen(true); }} className="bg-[#F85037] hover:bg-[#e04530] text-white gap-2" data-testid="create-event-btn">
              <Plus size={16} /> Create Event
            </Button>
          </div>
          <div className="flex items-center gap-2 text-xs text-zinc-500">
            <span>Sort by:</span>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[140px] h-8 border-[#E6E6E6] text-xs" data-testid="events-sort">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="upcoming">Upcoming first</SelectItem>
                <SelectItem value="newest">Newest first</SelectItem>
                <SelectItem value="popular">Most popular</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar */}
        <aside className="lg:w-64 shrink-0 space-y-4">
          <div className="bg-white border border-[#E6E6E6] rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-zinc-900" style={{ fontFamily: 'Outfit' }}>Filters</h3>
              {hasFilters && (
                <button onClick={clearFilters} className="text-xs text-[#074572] hover:underline font-medium" data-testid="events-clear-filters">Clear all</button>
              )}
            </div>

            <div className="relative mb-4">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search events..." className="pl-9 border-[#E6E6E6] h-9 text-sm bg-zinc-50/50" data-testid="events-search-input" />
            </div>

            <FilterGroup
              title="Status"
              items={statusItems}
              value={statusFilter}
              onChange={setStatusFilter}
              testId="filter-status"
            />

            <FilterGroup
              title="Event Type"
              items={typeItems}
              value={typeFilter}
              onChange={setTypeFilter}
              testId="filter-type"
            />

            <div className="pt-3 border-t border-[#E6E6E6]">
              <Label className="text-[11px] font-bold text-zinc-800 uppercase tracking-wider">Date</Label>
              <div className="relative mt-2">
                <Input type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} placeholder="Any date" className="border-[#E6E6E6] h-9 text-sm bg-white" data-testid="events-date-filter" />
              </div>
            </div>

            <div className="pt-3">
              <Label className="text-[11px] font-bold text-zinc-800 uppercase tracking-wider">Location</Label>
              <div className="relative mt-2">
                <MapPin size={13} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <Input value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)} placeholder="Any location" className="pr-9 border-[#E6E6E6] h-9 text-sm" data-testid="events-location-filter" />
              </div>
            </div>

            <div className="pt-3">
              <Label className="text-[11px] font-bold text-zinc-800 uppercase tracking-wider">Organizer</Label>
              <div className="relative mt-2">
                <Input value={organizerFilter} onChange={(e) => setOrganizerFilter(e.target.value)} placeholder="Any organizer" className="border-[#E6E6E6] h-9 text-sm" data-testid="events-organizer-filter" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-[#074572]/5 to-[#F85037]/5 border border-[#074572]/10 rounded-xl p-4 text-center" data-testid="hosting-cta">
            <div className="w-10 h-10 rounded-md bg-white border border-[#074572]/20 flex items-center justify-center mx-auto mb-2">
              <CalendarDays size={18} className="text-[#074572]" />
            </div>
            <p className="text-sm font-semibold text-zinc-900" style={{ fontFamily: 'Outfit' }}>Hosting an event?</p>
            <p className="text-[11px] text-zinc-500 mt-1">Create and manage your events with ease.</p>
            <Button
              onClick={() => { setEditEvent(null); setCreateOpen(true); }}
              variant="outline"
              className="w-full mt-3 border-[#F85037] text-[#F85037] hover:bg-[#F85037]/5 h-8 text-xs"
              data-testid="hosting-cta-btn"
            >
              Create Event
            </Button>
          </div>
        </aside>

        {/* Grid */}
        <div className="flex-1 min-w-0">
          {loading ? (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-72 bg-white border border-[#E6E6E6] rounded-xl animate-pulse" />
              ))}
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="text-center py-16 bg-white border border-dashed border-[#E6E6E6] rounded-xl text-zinc-400">
              <CalendarDays size={40} className="mx-auto mb-3 opacity-50" />
              <p className="font-medium">No events found</p>
              <p className="text-sm mt-1">Try clearing filters or create your own event.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5" data-testid="events-grid">
              {filteredEvents.map(event => (
                <EventCard
                  key={event.id}
                  event={event}
                  myId={myId}
                  onJoin={handleJoin}
                  onOpenDetail={openDetail}
                  onEdit={openEdit}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <Dialog open={createOpen} onOpenChange={(o) => { setCreateOpen(o); if (!o) setEditEvent(null); }}>
        <EventFormDialog
          editing={editEvent}
          onDone={() => { setCreateOpen(false); setEditEvent(null); loadEvents(); }}
          onDelete={handleDelete}
        />
      </Dialog>

      <EventDetailDialog open={detailOpen} onClose={() => setDetailOpen(false)} event={detailEvent} myId={myId} onJoinToggle={handleJoin} />
    </div>
  );
}

/* -------------------- CREATE / EDIT DIALOG -------------------- */
function EventFormDialog({ editing, onDone, onDelete }) {
  const [form, setForm] = useState({
    title: editing?.title || "",
    description: editing?.description || "",
    date: editing?.date ? new Date(editing.date).toISOString().slice(0, 16) : "",
    end_date: editing?.end_date ? new Date(editing.end_date).toISOString().slice(0, 16) : "",
    location: editing?.location || "",
    event_type: editing?.event_type || "match",
    online_url: editing?.online_url || "",
    max_participants: editing?.max_participants || "",
    organizer_display_name: editing?.organizer_display_name || "",
  });
  const [sponsors, setSponsors] = useState(editing?.sponsors || []);
  const [sponsorInput, setSponsorInput] = useState({ name: "", logo_url: "" });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setImageFile(f);
    setImagePreview(URL.createObjectURL(f));
  };
  const removeImage = () => {
    setImageFile(null);
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview(null);
  };

  const addSponsor = () => {
    if (!sponsorInput.name.trim()) return;
    setSponsors(prev => [...prev, { name: sponsorInput.name.trim(), logo_url: sponsorInput.logo_url.trim() }]);
    setSponsorInput({ name: "", logo_url: "" });
  };
  const removeSponsor = (i) => setSponsors(prev => prev.filter((_, idx) => idx !== i));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim() || !form.date || !form.location.trim()) {
      toast.error("Please fill in all required fields"); return;
    }
    setSubmitting(true);
    try {
      let imageUrl = editing?.image_url || "";
      if (imageFile) {
        setUploading(true);
        const fd = new FormData();
        fd.append("file", imageFile);
        const { data: up } = await api.post("/events/image-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        imageUrl = up.image_url;
        setUploading(false);
      }
      const payload = {
        title: form.title, description: form.description, date: form.date,
        end_date: form.end_date || null,
        location: form.location, event_type: form.event_type,
        online_url: form.online_url || null,
        max_participants: form.max_participants ? parseInt(form.max_participants) : null,
        image_url: imageUrl || null,
        organizer_display_name: form.organizer_display_name || null,
        sponsors,
      };
      if (editing) {
        await api.patch(`/events/${editing.id}`, payload);
        toast.success("Event updated!");
      } else {
        await api.post("/events", payload);
        toast.success("Event created!");
      }
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSubmitting(false); setUploading(false); }
  };

  return (
    <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle style={{ fontFamily: 'Outfit' }}>{editing ? "Edit Event" : "Create Event"}</DialogTitle>
      </DialogHeader>
      <form onSubmit={handleSubmit} className="space-y-3 mt-2">
        <div><Label>Title*</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 border-[#E6E6E6]" data-testid="event-title-input" required /></div>
        <div><Label>Description*</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 border-[#E6E6E6] min-h-[70px]" data-testid="event-desc-input" required /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Start Date & Time*</Label><Input type="datetime-local" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1 border-[#E6E6E6]" data-testid="event-date-input" required /></div>
          <div><Label>End Date & Time (optional)</Label><Input type="datetime-local" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1 border-[#E6E6E6]" data-testid="event-end-date-input" /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Location*</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="City · Venue" className="mt-1 border-[#E6E6E6]" data-testid="event-location-input" required /></div>
          <div><Label>Max Participants</Label><Input type="number" value={form.max_participants} onChange={(e) => setForm({ ...form, max_participants: e.target.value })} placeholder="Optional" className="mt-1 border-[#E6E6E6]" data-testid="event-max-input" /></div>
        </div>
        <div>
          <Label>Type</Label>
          <Select value={form.event_type} onValueChange={(v) => setForm({ ...form, event_type: v })}>
            <SelectTrigger className="mt-1 border-[#E6E6E6]" data-testid="event-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {EVENT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="flex items-center gap-1.5"><Building2 size={13} className="text-[#074572]" /> Organizer Display Name (optional)</Label>
          <Input value={form.organizer_display_name} onChange={(e) => setForm({ ...form, organizer_display_name: e.target.value })} placeholder="e.g. PAOK Academy, SportsUnity Community" className="mt-1 border-[#E6E6E6]" data-testid="event-organizer-name" />
          <p className="text-[10px] text-zinc-400 mt-1">Override your personal name with a team/company. Empty = use your profile name.</p>
        </div>
        <div>
          <Label className="flex items-center gap-1.5"><Video size={13} className="text-[#074572]" /> Online Session Link (optional)</Label>
          <Input value={form.online_url} onChange={(e) => setForm({ ...form, online_url: e.target.value })} placeholder="https://zoom.us/j/... or https://meet.google.com/..." className="mt-1 border-[#E6E6E6]" data-testid="event-online-url" />
        </div>
        <div>
          <Label>Cover Image</Label>
          {(imagePreview || (editing?.image_url && !imageFile)) ? (
            <div className="relative mt-2">
              <img src={imagePreview || (editing.image_url.startsWith("http") ? editing.image_url : getImageUrl(editing.image_url))} alt="preview" className="w-full max-h-40 object-cover rounded-md border border-[#E6E6E6]" />
              <button type="button" onClick={removeImage} className="absolute -top-2 -right-2 w-6 h-6 bg-zinc-900 text-white rounded-full flex items-center justify-center hover:bg-[#F85037]" data-testid="event-remove-image">
                <X size={12} />
              </button>
            </div>
          ) : (
            <label htmlFor="event-image-input" className="mt-1 flex items-center gap-2 px-3 py-2 border border-dashed border-[#E6E6E6] rounded-md cursor-pointer hover:border-[#074572] text-sm text-zinc-500" data-testid="event-image-label">
              <Upload size={14} /> Click to upload an image (1:1 recommended)
              <input id="event-image-input" type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid="event-image-input" />
            </label>
          )}
        </div>
        <div>
          <Label className="flex items-center gap-1.5"><Sparkles size={13} className="text-[#F85037]" /> Sponsors (optional)</Label>
          <div className="mt-1 space-y-1.5">
            {sponsors.map((s, i) => (
              <div key={i} className="flex items-center gap-2 text-xs bg-zinc-50 border border-[#E6E6E6] rounded-md px-2 py-1.5">
                <span className="flex-1 truncate">{s.name}{s.logo_url ? " · logo" : ""}</span>
                <button type="button" onClick={() => removeSponsor(i)} className="text-zinc-400 hover:text-[#F85037]" data-testid={`sponsor-remove-${i}`}>
                  <X size={12} />
                </button>
              </div>
            ))}
            <div className="flex gap-2">
              <Input value={sponsorInput.name} onChange={(e) => setSponsorInput({ ...sponsorInput, name: e.target.value })} placeholder="Sponsor name" className="flex-1 border-[#E6E6E6] h-8 text-xs" data-testid="sponsor-name-input" />
              <Input value={sponsorInput.logo_url} onChange={(e) => setSponsorInput({ ...sponsorInput, logo_url: e.target.value })} placeholder="Logo URL (optional)" className="flex-1 border-[#E6E6E6] h-8 text-xs" data-testid="sponsor-logo-input" />
              <Button type="button" size="sm" onClick={addSponsor} variant="outline" className="h-8 text-xs" data-testid="sponsor-add-btn">Add</Button>
            </div>
          </div>
        </div>
        <Button type="submit" disabled={submitting || uploading} className="w-full bg-[#074572] hover:bg-[#053a60] text-white" data-testid="event-submit-btn">
          {uploading ? "Uploading image..." : submitting ? "Saving..." : (editing ? "Save Changes" : "Create Event")}
        </Button>
        {editing && onDelete && (
          <Button type="button" variant="outline" onClick={() => { onDelete(editing); onDone(); }} className="w-full border-[#F85037]/30 text-[#F85037] hover:bg-[#F85037]/5" data-testid="event-form-delete-btn">
            <Trash2 size={13} className="mr-1.5" /> Delete Event
          </Button>
        )}
      </form>
    </DialogContent>
  );
}

/* -------------------- DETAIL DIALOG -------------------- */
function EventDetailDialog({ open, onClose, event, myId, onJoinToggle }) {
  if (!event) return null;
  const joined = event.participants?.includes(myId);
  const img = event.image_url ? (event.image_url.startsWith("http") ? event.image_url : getImageUrl(event.image_url)) : placeholderImages[0];
  const isOnline = !!event.online_url;
  const cap = event.max_participants;
  const attendees = event.attendees || [];
  const isPast = event.date && new Date(event.date) < new Date();
  const isFull = cap && (event.participants?.length || 0) >= cap;
  const disabled = isPast || (isFull && !joined);
  const orgName = event.organizer_display_name || event.organizer?.name || event.organizer_name;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto p-0" data-testid="event-detail-dialog">
        <div className={`h-56 bg-zinc-100 overflow-hidden relative ${isPast ? "grayscale" : ""}`}>
          <img src={img} alt={event.title} className="w-full h-full object-cover" />
          {isPast && (
            <div className="absolute top-3 right-3 bg-zinc-700 text-white text-xs font-bold px-2.5 py-1 rounded-md uppercase tracking-wider">Completed</div>
          )}
          {!isPast && isFull && (
            <div className="absolute top-3 right-3 bg-[#F85037] text-white text-xs font-bold px-2.5 py-1 rounded-md uppercase tracking-wider">Sold Out</div>
          )}
          {isOnline && (
            <div className="absolute top-3 left-3 bg-[#074572] text-white text-xs font-semibold px-2.5 py-1 rounded-md flex items-center gap-1.5">
              <Video size={12} /> Online Session
            </div>
          )}
        </div>
        <div className="p-6">
          <DialogHeader className="text-left">
            <Badge className={`text-[10px] ${eventTypeColors[event.event_type] || "bg-zinc-100 text-zinc-600"} capitalize self-start mb-2 w-fit`}>
              {EVENT_TYPES.find(t => t.value === event.event_type)?.label || event.event_type}
            </Badge>
            <DialogTitle className="text-2xl" style={{ fontFamily: 'Outfit' }}>{event.title}</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
            <InfoBlock Icon={CalendarDays} label="detail-date" value={new Date(event.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })} sub={new Date(event.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} />
            <InfoBlock Icon={MapPin} label="detail-location" value={event.location} sub="Location" />
            <InfoBlock Icon={Users} label="detail-attendees" value={`${event.participants?.length || 0}${cap ? ` / ${cap}` : ""}`} sub="Attending" />
          </div>

          {isOnline && (
            <a href={event.online_url} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-2 px-3 py-2 bg-[#074572]/5 border border-[#074572]/20 rounded-md text-sm text-[#074572] hover:bg-[#074572]/10 transition-colors" data-testid="event-online-link">
              <Video size={14} /> Join online session <ExternalLink size={12} />
            </a>
          )}

          <div className="mt-5">
            <h3 className="text-xs uppercase tracking-wider text-zinc-400 font-semibold mb-2">About</h3>
            <p className="text-sm text-zinc-700 whitespace-pre-line">{event.description}</p>
          </div>

          {orgName && (
            <div className="mt-5">
              <h3 className="text-xs uppercase tracking-wider text-zinc-400 font-semibold mb-2">Organized by</h3>
              <Link to={event.organizer?._id ? `/profile/${event.organizer._id}` : "#"} className="flex items-center gap-3 p-3 bg-zinc-50 border border-[#E6E6E6] rounded-md hover:border-[#074572]/30 transition-colors" data-testid="event-detail-organizer">
                <Avatar className="h-10 w-10">
                  {event.organizer?.profile_image ? <AvatarImage src={getImageUrl(event.organizer.profile_image)} /> : null}
                  <AvatarFallback className="bg-[#074572] text-white text-xs font-medium">{orgName?.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2)}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-zinc-900">{orgName}</p>
                  <p className="text-[11px] text-zinc-500 capitalize">{event.organizer?.role}{event.organizer?.club ? ` · ${event.organizer.club}` : ""}</p>
                </div>
              </Link>
            </div>
          )}

          {(event.sponsors || []).length > 0 && (
            <div className="mt-5">
              <h3 className="text-xs uppercase tracking-wider text-zinc-400 font-semibold mb-2">Sponsors</h3>
              <div className="flex flex-wrap items-center gap-4 bg-zinc-50 border border-[#E6E6E6] rounded-md p-3">
                {event.sponsors.map((s, i) => (
                  <div key={i} className="flex items-center gap-1.5 text-xs">
                    {s.logo_url ? <img src={s.logo_url.startsWith("http") ? s.logo_url : getImageUrl(s.logo_url)} alt={s.name} className="h-6 w-auto max-w-[80px] object-contain" /> : <span className="font-semibold text-zinc-700">{s.name}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-5">
            <h3 className="text-xs uppercase tracking-wider text-zinc-400 font-semibold mb-2">Attendees ({attendees.length})</h3>
            {attendees.length === 0 ? (
              <p className="text-sm text-zinc-400">No attendees yet</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {attendees.map(a => (
                  <Link key={a._id} to={`/profile/${a._id}`} className="flex items-center gap-2 p-2 bg-white border border-[#E6E6E6] rounded hover:border-[#074572]/30 transition-colors" data-testid={`event-attendee-${a._id}`}>
                    <Avatar className="h-8 w-8">
                      {a.profile_image ? <AvatarImage src={getImageUrl(a.profile_image)} /> : null}
                      <AvatarFallback className="bg-[#074572] text-white text-[10px] font-medium">{a.name?.split(" ").map(n => n[0]).join("").toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-zinc-900 truncate">{a.name}</p>
                      <p className="text-[10px] text-zinc-500 capitalize truncate">{a.role}{a.position ? ` · ${a.position}` : ""}</p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {!isPast && (
            <Button
              className={`w-full mt-6 ${joined ? "bg-[#074572] hover:bg-[#053a60]" : "bg-[#F85037] hover:bg-[#e04530]"} text-white`}
              onClick={() => onJoinToggle(event.id)}
              disabled={disabled}
              data-testid="event-detail-join-btn"
            >
              {isFull && !joined ? "Sold Out" : (joined ? "Leave Event" : "Join Event")}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
